/**
 * 캠페인 컨텍스트 sessionStorage 관리
 *
 * 추적 키 7종:
 *   - utm_source / utm_medium / utm_campaign / utm_content / utm_term
 *   - template_code (푸시·알림톡·친구톡 템플릿 식별)
 *   - prev_event_type (직전 이벤트명)
 *
 * 데이터 소스 우선순위:
 *   1. URL query param (첫 진입 시 최우선) - saveCampaignContext()
 *   2. WebView 브릿지 - fillMissingCampaignContext() (안 채워진 키만)
 *   3. 기존 sessionStorage - 위 둘 다 새 값 없으면 유지
 *
 * 탭 닫으면 sessionStorage 자체 만료 → 캠페인 컨텍스트도 같이 사라짐 (의도된 동작).
 */

const SESSION_KEY = 'gov_benefit_session';

const TRACKED_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'template_code',
  'prev_event_type',
] as const;

export type TrackedKey = (typeof TRACKED_KEYS)[number];
export type CampaignContext = Partial<Record<TrackedKey, string>>;

const TRACKED_KEY_SET: ReadonlySet<string> = new Set(TRACKED_KEYS);

function isTrackedKey(key: string): key is TrackedKey {
  return TRACKED_KEY_SET.has(key);
}

export function parseUrlParams(search: string): CampaignContext {
  const result: CampaignContext = {};
  try {
    const params = new URLSearchParams(search);
    for (const key of TRACKED_KEYS) {
      const raw = params.get(key);
      if (raw === null) continue;
      const trimmed = raw.trim();
      if (trimmed.length === 0) continue;
      result[key] = trimmed;
    }
  } catch {
    // URLSearchParams 파싱 실패 - 빈 객체 반환
  }
  return result;
}

function pickTrackedFromObject(
  props: Record<string, unknown>
): CampaignContext {
  const result: CampaignContext = {};
  for (const [k, v] of Object.entries(props)) {
    if (!isTrackedKey(k)) continue;
    if (v === null || v === undefined) continue;
    const str = typeof v === 'string' ? v : String(v);
    const trimmed = str.trim();
    if (trimmed.length === 0) continue;
    result[k] = trimmed;
  }
  return result;
}

export function getCampaignContext(): CampaignContext {
  try {
    if (typeof sessionStorage === 'undefined') return {};
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return {};
    return pickTrackedFromObject(parsed as Record<string, unknown>);
  } catch {
    return {};
  }
}

function writeCampaignContext(ctx: CampaignContext): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    if (Object.keys(ctx).length === 0) return;
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(ctx));
  } catch {
    // 무해화
  }
}

/**
 * sessionStorage 병합 저장 — 신규 값이 있으면 덮어쓰기.
 * URL 파라미터로 갱신할 때 호출 (URL이 새 진입에서 최우선).
 */
export function saveCampaignContext(props: CampaignContext): void {
  const sanitized = pickTrackedFromObject(props as Record<string, unknown>);
  if (Object.keys(sanitized).length === 0) return;
  const existing = getCampaignContext();
  const merged: CampaignContext = { ...existing, ...sanitized };
  writeCampaignContext(merged);
}

const PASSTHROUGH_KEYS: readonly TrackedKey[] = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'template_code',
];

function isSamsamzHost(hostname: string): boolean {
  return hostname === '3o3.co.kr' || hostname.endsWith('.3o3.co.kr');
}

/**
 * 3o3.co.kr 도메인 링크에 캠페인 컨텍스트를 query로 append.
 * - 외부 도메인이면 그대로 반환 (UTM 누수 방지)
 * - 이미 URL에 같은 키가 있으면 덮어쓰지 않음
 * - UTM 5종 + template_code 만 전달. prev_event_type은 의미 꼬임 방지를 위해 제외.
 *
 * 탭/origin 경계를 넘는 CTA에서 캠페인 컨텍스트를 보존하려면 이 함수로 href를 감싸야 함.
 */
export function appendCampaignToUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    if (!isSamsamzHost(parsed.hostname)) return url;
    const ctx = getCampaignContext();
    let changed = false;
    for (const key of PASSTHROUGH_KEYS) {
      const value = ctx[key];
      if (!value) continue;
      if (parsed.searchParams.has(key)) continue;
      parsed.searchParams.set(key, value);
      changed = true;
    }
    if (!changed) return url;
    return parsed.toString();
  } catch {
    return url;
  }
}

/**
 * 안 채워진 키만 채우기 — 기존 sessionStorage 값은 절대 덮어쓰지 않음.
 * WebView 브릿지 응답을 URL보다 낮은 우선순위로 머지할 때 사용.
 */

export function fillMissingCampaignContext(props: CampaignContext): void {
  const sanitized = pickTrackedFromObject(props as Record<string, unknown>);
  if (Object.keys(sanitized).length === 0) return;
  const existing = getCampaignContext();
  const merged: CampaignContext = { ...existing };
  let changed = false;
  for (const [k, v] of Object.entries(sanitized)) {
    const key = k as TrackedKey;
    if (merged[key] !== undefined) continue;
    merged[key] = v;
    changed = true;
  }
  if (!changed) return;
  writeCampaignContext(merged);
}
